import { createVeriffFrame, MESSAGES } from "@veriff/incontext-sdk";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import BackBtnWithLabel from "../../../components/common/backBtnWithLabel/backBtnWithLabel";
import StickyActionBar from "../../../components/common/stickyActionBar/StickyActionBar";
import Button from "../../../components/common/Button/Button";
import { CircleAlert, Info, ShieldCheck } from "lucide-react";
import { Card } from "../../../components/ui/card";
import TitleDescription from "../../../components/newMotorPolicy/titleDescription/titleDescription";
import { db } from "../../../firebase";
import useFirestoreDoc from "../../../hooks/useFirestoreDoc";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import useScreenResolution from "../../../hooks/useScreenResolution";
import { verifySession } from "../../../redux/features/signUp/createVeriffSessionSlice";
import { selectCprFromKycVerification } from "../../../redux/features/signUp/kycVerificationStatusSlice";
import "./GetVerifiedPage.css";

const GetVerifiedPage = ({ onSuccess, onBackClick, pinActionOnMobile = false }) => {
  const { mobile } = useScreenResolution();
  const translate = useLanguageTranslate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [sessionStatus, setSessionStatus] = useState(null);

  const { signupDetails } = useSelector((state) => state.signup);
  const cprFromVerifKyc = useSelector(selectCprFromKycVerification);
  const cprNumber = signupDetails?.cprNumber || cprFromVerifKyc;

  const { data, loading } = useFirestoreDoc(db, "veriff-decisions", cprNumber);

  useEffect(() => {
    if (data?.status == "approved") {
      // toast.success("Your Kyc Verification has been approved")
      setIsLoading(false);
      onSuccess();
    }
  }, [data]);

  const [isLoading, setIsLoading] = useState(true);

  const initiateVerification = async () => {
    const payload = signupDetails?.cprNumber || cprFromVerifKyc;
    setIsLoading(true);
    const response = await dispatch(verifySession(payload));
    setIsLoading(false);
    if (response?.payload?.verification?.url) {
      const redirectUrl = response?.payload?.verification?.url;
      if (redirectUrl) {
        const veriffFrame = createVeriffFrame({
          url: redirectUrl,
          onEvent: async (msg) => {
            switch (msg) {
              case MESSAGES.CANCELED:
                setSessionStatus("CANCELED");
                break;

              case MESSAGES.FINISHED:
                //await handleKycVerificationStatus();
                setIsLoading(true);
                break;
              default:
                console.log("Unhandled event: ", msg);
                break;
            }
          },
        });
      } else {
        toast.error(
          translate(
            "Unable to get the verification URL. Please try again.",
            "غير قادر على الحصول على رابط التحقق. الرجاء المحاولة مرة أخرى."
          )
        );
      }
    } else {
      toast.error(
        translate(
          response?.payload?.ErrorMessageEn,
          response?.payload?.ErrorMessageAr
        )
      );
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      initiateVerification();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, []);

  const retryVerification = () => {
    setSessionStatus(null);
    initiateVerification();
  };

  console.log(data, "some data");
  return (
    <div className="verify_otp_page mt-20 flex h_100 p-0 justify-center items-center mb-3 max767:p-5 max767:mt-[60px] w-[500px] max-w-[100%] mx-auto">
      {/* <Loader /> */}
      <div className="get_verified_page verify-content">
        <div className="content flex flex-col align-middle justify-center">
          {/* In the sign-up flow the flow's own Back steps back a page; the
              sign-up arrow here went home and brought a stray "Sign up" label. */}
          {mobile && !onBackClick && (
            <BackBtnWithLabel label={translate("Sign up", "التسجيل")} />
          )}
          <TitleDescription
            className="mb-6"
            title={t("signUpFlow.getVerifiedPage.page_title")}
            description={t("signUpFlow.getVerifiedPage.page_description")}
          />
        </div>

        {/* Status card. Two honest states: working (preparing the session,
            then waiting on the provider's decision) and failed. No fake
            progress: the provider reports nothing until it decides, so the
            working state says what is happening and how long it usually
            takes, and a slow ring shows it is live. Keyed by state so the
            switch cross-fades rather than snapping. */}
        {(() => {
          const failed = sessionStatus == "CANCELED" || data?.status == "declined";
          return (
            <Card
              key={failed ? "failed" : "working"}
              role="status"
              aria-live="polite"
              aria-busy={!failed && (isLoading || loading)}
              className="mb-6 overflow-hidden rounded-[14px] border-[#E5E9EB] shadow-none animate-[claimStepFadeIn_200ms_ease-out]"
            >
              <div className="flex items-center gap-4 p-5">
                <span className="relative flex h-11 w-11 shrink-0 items-center justify-center">
                  {/* The whole of this step is a wait (the provider's window,
                      then its decision), so the working state always spins:
                      a brand-blue arc on a light track around the icon. Brisk,
                      because a quick spinner makes the same wait feel shorter;
                      under reduced motion it slows rather than stops, since
                      here the motion is the message. */}
                  {!failed && (
                    <span
                      aria-hidden="true"
                      className="absolute -inset-[5px] rounded-full border-[2.5px] border-[#D4EEFA] border-t-[#00A9E7] animate-spin [animation-duration:900ms] motion-reduce:[animation-duration:2s]"
                    />
                  )}
                  <span
                    className={`relative flex h-11 w-11 items-center justify-center rounded-full ${
                      failed ? "bg-[#FEF1F0]" : "bg-[#E6F6FD]"
                    }`}
                  >
                    {failed ? (
                      <CircleAlert aria-hidden="true" className="h-[22px] w-[22px] text-[#D93F35]" />
                    ) : (
                      <ShieldCheck aria-hidden="true" className="h-[22px] w-[22px] text-[#0A7CAE]" />
                    )}
                  </span>
                </span>
                <div className="min-w-0 pt-0.5">
                  <p className="text-[#1A2024] text-base font-semibold leading-6 tracking-[-0.16px]">
                    {failed
                      ? data?.messageEn && data?.messageAr
                        ? translate(data.messageEn, data.messageAr)
                        : t("signUpFlow.getVerifiedPage.verification_failed_title")
                      : t("signUpFlow.getVerifiedPage.verify_your_account_label")}
                  </p>
                  <p className="mt-1 text-[#5B6871] text-sm leading-5">
                    {failed
                      ? data?.subMsgEn && data?.subMsgAr
                        ? translate(data.subMsgEn, data.subMsgAr)
                        : t("signUpFlow.getVerifiedPage.verification_failed_description")
                      : t("signUpFlow.getVerifiedPage.dont_close_label")}
                  </p>
                </div>
              </div>
              {!failed && (
                <div className="flex items-center gap-2 border-t border-[#E5E9EB] bg-[#F8FAFB] px-5 py-3 text-[#48535B] text-sm leading-5">
                  <Info aria-hidden="true" className="h-4 w-4 shrink-0 text-[#6C7A83]" />
                  {t("signUpFlow.getVerifiedPage.keep_open_label")}
                </div>
              )}
            </Card>
          );
        })()}
        {(sessionStatus == "CANCELED" ||
          (data?.status == "declined" && data?.attempt < 4)) && (
          <StickyActionBar pinned={pinActionOnMobile}>
            <Button
              label={t("signUpFlow.getVerifiedPage.btn_label")}
              className="sign-in-btn primary-button"
              onSubmit={retryVerification}
            />
          </StickyActionBar>
        )}

        {/* <Button
          label={"Start Verification"}
          className="sign-in-btn primary-button"
          onSubmit={retryVerification}
        /> */}
      </div>
    </div>
  );
};

export default GetVerifiedPage;
